
import { computed, defineComponent, inject, onMounted, reactive, ref } from 'vue';
import { useDialogWithRouter } from '@/compositions/useDialogWithRouter';
import CustomerCoupon from '@/components/organisms/coupon/CustomerCoupon.vue';
import { MultipleCouponKey } from '@/compositions/useMultipleCoupon';
import { useShop } from '@/compositions/useShop';
import VueQrcode from 'vue-qrcode';
import { CustomerCoupon as TypeCustomerCoupon } from '@/repositories/couponRepository';
import DialogSelectCouponAvailableShop from '@/components/organisms/coupon/content/DialogSelectCouponAvailableShop.vue';
import DialogQRCodeReader from '@/components/organisms/DialogQRCodeReader.vue';
import ComsbiSaasScheme from '@comsbi/common/src/libs/comsbi-saas-scheme';
import DialogConfirm from '@/components/molecules/DialogConfirm.vue';
import { Coupon } from '@comsbi/common/src/types/models';
import { useRoute, useRouter } from 'vue-router/composables';
import { i18n } from '@/plugins/i18n';
import { ToastProgrammatic as Toast } from 'buefy';
import DisplayCurrentTimer from '@/components/atoms/DisplayCurrentTimer.vue';

const CHANNEL_ID =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

type TabType = 'reader' | 'qr' | 'self';
type State = {
  tab: TabType | null;
  shopId?: string;
  agree: boolean;
  isLoading: boolean;
  step: 'CONFIRM' | 'COMPLETE';
};

export default defineComponent({
  name: 'DialogCouponUsingMultiple',

  components: {
    DialogQRCodeReader,
    DialogSelectCouponAvailableShop,
    DialogConfirm,
    CustomerCoupon,
    VueQrcode,
    DisplayCurrentTimer,
  },

  setup(_, { emit }) {
    const router = useRouter();
    const multipleCouponInjection = inject(MultipleCouponKey);
    if (!multipleCouponInjection) throw new Error(`NO PROVIDE ${MultipleCouponKey}`);
    const { customerCoupons: _customerCoupons, length, useMultipleCustomerCoupon } = multipleCouponInjection;
    // クーポン使用後にもプレビューを継続したいのでコピーしておく（元データがクリアされるため）
    const customerCoupons = [..._customerCoupons.value];
    const couponIds = customerCoupons.map(customerCoupon => customerCoupon.couponId);

    // クーポンが選択されていない場合は一覧へ
    if (!length.value) router.replace('/coupon');

    const { dialog, open, close, afterClose } = useDialogWithRouter(router);
    const { couponCustomerSettings, getDetail } = useShop();

    const customerCouponRef = ref<InstanceType<typeof CustomerCoupon>[]>();
    const dialogConfirmRef = ref<InstanceType<typeof DialogConfirm>>();

    const state = reactive<State>({
      tab: couponCustomerSettings.value.shopQREnabled
        ? 'reader'
        : couponCustomerSettings.value.shopAppEnabled
        ? 'qr'
        : 'self',
      shopId: undefined,
      agree: false,
      isLoading: false,
      step: 'CONFIRM',
    });

    const coupons = computed(() => {
      return customerCoupons.filter(customerCoupons => {
        const shopIds = customerCoupons.coupon.shopIds;
        return !state.shopId || !shopIds || shopIds.length === 0 || shopIds.includes(state.shopId);
      });
    });

    const qrCodeValue = computed(() => {
      // クーポン使用QR
      return JSON.stringify({
        type: 'coupon', // 固定 CS-7411
        customerId: customerCoupons[0]?.customerId,
        customerCouponId: customerCoupons.map(coupon => coupon.id),
        action: 'use', // 固定
      });
    });

    const endImageUrl = (customerCoupon: TypeCustomerCoupon) => {
      try {
        return JSON.parse(customerCoupon.couponMaster.attrs).endImageUrl || null;
      } catch {
        return null;
      }
    };

    /**
     * 手動利用可能な状態かどうか（店舗選択済み && 同意済み）
     * CS-4882 クーポン利用設定
     */
    const available = computed(() => {
      if (!couponCustomerSettings.value.manualEnabled) return false;
      if (couponCustomerSettings.value.manualShopSelectable && !state.shopId) return false;
      if (couponCustomerSettings.value.manualAgreementRequired && !state.agree) return false;
      return true;
    });

    /**
     * クーポンの割引合計金額
     *
     * CS-9351 対象店舗ではないものは計算に含めない
     */
    const totalDiscountAmount = computed(() => {
      return coupons.value.reduce((acc, customerCoupon) => {
        const parsedAttrs = JSON.parse(customerCoupon.couponMaster.attrs);
        const isDiscountType = parsedAttrs.discount.type === 'CONST';
        if (!isDiscountType) return acc;
        // CS-9307 https://sonicmoov.slack.com/archives/C08GVRC7LBA/p1747104803282229?thread_ts=1744682992.490299&cid=C08GVRC7LBA
        // 円の場合は加算しない
        const hasOnlyYen = parsedAttrs.discount.options && parsedAttrs.discount.options.onlyYen;
        if (hasOnlyYen) return acc;

        return acc + parsedAttrs.discount.value;
      }, 0);
    });

    /**
     * クーポンの割引の合計金額 (onlyYenがtrue)
     * CS-9307
     *
     * CS-9351 対象店舗ではないものは計算に含めない
     */
    const totalDiscountOnlyYenAmount = computed(() => {
      return coupons.value.reduce((acc, customerCoupon) => {
        const parsedAttrs = JSON.parse(customerCoupon.couponMaster.attrs);
        const isDiscountType = parsedAttrs.discount.type === 'CONST';
        if (!isDiscountType) return acc;
        // 円の場合は加算する
        const hasOnlyYen = parsedAttrs.discount.options && parsedAttrs.discount.options.onlyYen;
        if (!hasOnlyYen) return acc;

        return acc + parsedAttrs.discount.value;
      }, 0);
    });

    /**
     * クーポンを使用（消費）する
     * @param {boolean} isDialog 確認ダイアログを挟むかどうか
     */
    const consumeCoupon = async (isDialog: boolean) => {
      state.isLoading = true;

      const invalidCoupons = customerCoupons.filter(coupon => {
        const shopIds = coupon.coupon.shopIds;

        return shopIds && shopIds.length > 0 && !shopIds.includes(state.shopId!);
      });

      let invalidCouponMessage: string | undefined;

      if (invalidCoupons.length > 0) {
        invalidCouponMessage = `以下のクーポンは対象店舗外のため使用されません。\n\n${invalidCoupons
          .map(coupon => `${coupon.couponMaster.name}`)
          .join('\n')}`;
      }

      const hasConstDiscount = totalDiscountAmount.value > 0;
      const hasConstOnlyYenDiscount = totalDiscountOnlyYenAmount.value > 0;

      // 確認モーダーのメッセージ
      const baseMessage = `${coupons.value.length}枚使用します。\nよろしいですか？\n`;
      const constMessage = `${hasConstDiscount ? `\n割引額: ${totalDiscountAmount.value.toLocaleString()}円` : ''}`;
      const constOnlyYenMessage = `${
        hasConstOnlyYenDiscount ? `\n金額: ${totalDiscountOnlyYenAmount.value.toLocaleString()}円` : ''
      }`;

      let rateMessage = '';
      // RATEタイプのクーポンが存在する場合に、割引率をグループ化してメッセージを生成
      const rateCoupons = coupons.value.filter(customerCoupon => {
        const parsedAttrs = JSON.parse(customerCoupon.couponMaster.attrs);
        return parsedAttrs.discount.type === 'RATE';
      });
      if (rateCoupons.length > 0) {
        const rateMap = rateCoupons.reduce((acc, customerCoupon) => {
          const parsedAttrs = JSON.parse(customerCoupon.couponMaster.attrs);
          const rate = parsedAttrs.discount.value;
          acc[rate] = (acc[rate] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);
        rateMessage = '\n割引率:';
        Object.entries(rateMap).forEach(([rate, count]) => {
          rateMessage += `\n・${rate}% ${count}枚`;
        });
      }

      const title = `${i18n.t('keyword.coupon')}の使用`;
      const message =
        coupons.value.length > 0
          ? `${baseMessage}${constMessage}${constOnlyYenMessage}${rateMessage}`
          : '使用可能なクーポンが選択されていません。';

      const confirmed = isDialog
        ? await dialogConfirmRef.value?.open(
            title,
            message,
            {
              color: 'primary',
              okBtnColor: 'primary',
              width: 320,
              hideCancel: false,
              okBtnDisabled: !(coupons.value.length > 0),
            },
            invalidCouponMessage
          )
        : true;

      if (!confirmed) {
        state.isLoading = false;
        return;
      }

      try {
        // クーポン使用
        await useMultipleCustomerCoupon(coupons.value, state.shopId);

        state.step = 'COMPLETE';
        Toast.open({
          message: '使用しました',
          type: 'is-success',
          position: 'is-top',
        });

        customerCouponRef.value?.forEach(ref => ref.stamp()); // 使用済みスタンプを押す
        emit('used'); // 使用完了イベントを送出して、顧客クーポン一覧を再読み込みする
      } catch (e) {
        emit('error', e); // エラーイベントを送出
        Toast.open({
          message: '使用に失敗しました',
          type: 'is-warning',
          position: 'is-top',
        });
        throw e;
      } finally {
        state.isLoading = false;
      }
    };

    const refDialogQRCodeReader = ref<InstanceType<typeof DialogQRCodeReader>>();
    /**
     * クーポン利用店舗判別QR
     * @param value
     */
    const parseQRValue = async (value: string) => {
      if (!value) return;
      try {
        if (value.indexOf('cs:') !== 0) throw new Error('invalid value. value:' + value);
        const [modelKey, modelId] = ComsbiSaasScheme.parse(value);
        if (modelKey !== ComsbiSaasScheme.SHOP) throw new Error('invalid value. value:' + value);

        // 店舗指定
        state.shopId = modelId;

        // 他LINEアカウントの店舗か判定
        //  => CS-6942 すべてが組織クーポンの場合はLINEチャネル関係なく消費させる。チャネルクーポンを含む場合は店舗を制限する
        if (customerCoupons.find(v => v.couponMaster.channelId)) {
          const shopChannel = await getDetail(state.shopId);
          const channelId = shopChannel.channel.connection.items[0].toId;
          if (channelId && channelId !== CHANNEL_ID) {
            Toast.open({
              message: `他LINEアカウントの${i18n.t('keyword.coupon')}です`,
              type: 'is-warning',
              position: 'is-top',
            });
            return;
          }
        }
        // 使用
        consumeCoupon(true);
      } catch (e) {
        console.warn(e);
        Toast.open({
          message: 'QRコードの読み取りに失敗しました。',
          type: 'is-warning',
          position: 'is-top',
        });
      }
    };

    const numOfTabs = computed(() => {
      return [
        couponCustomerSettings.value.shopQREnabled,
        couponCustomerSettings.value.shopAppEnabled,
        couponCustomerSettings.value.manualEnabled,
      ].filter(v => v).length;
    });

    onMounted(async () => {
      open();
    });

    return {
      // dialog
      dialog,
      dialogConfirmRef,
      close,
      afterClose,

      couponCustomerSettings,
      numOfTabs,

      customerCouponRef,
      state,
      qrCodeValue,
      endImageUrl,
      available,
      consumeCoupon,
      customerCoupons,
      couponIds,

      // CS-4882
      refDialogQRCodeReader,
      parseQRValue,

      // CS-6329
      // CS-9351 対象店舗のID取得のロジック変更 
      filterShopIds: computed(() => {
        // 全店舗のクーポンは省く
        const haveShopIdCoupons = customerCoupons.filter(
          coupon => coupon.coupon.shopIds && coupon.coupon.shopIds.length > 0
        );

        // クーポンが一枚だけの場合はそのクーポンの店舗IDを返す
        if (haveShopIdCoupons.length === 1) return haveShopIdCoupons[0].coupon.shopIds!;

        const seenShopIds = new Set<string>();
        const duplicatShopIds = new Set<string>();

        haveShopIdCoupons.forEach(coupon => {
          coupon.coupon.shopIds!.forEach(shopId => {
            if (seenShopIds.has(shopId)) {
              duplicatShopIds.add(shopId);
            } else {
              seenShopIds.add(shopId);
            }
          });
        });

        return Array.from(duplicatShopIds);
      }),

      // CS-9307
      totalDiscountAmount,

      // CS-9351
      coupons,
    };
  },
});
